import PropTypes from 'prop-types';

import { theme } from '@prose-ui';
import { ClassNames, css, keyframes, legacyTheme, styled } from '@prose-ui/legacy';

import { Rating } from 'Components/Rating';
import { breakpoints } from 'Components/ThemeProvider';
import { Typography } from 'Components/Typography';
import { ConsultationFooter } from '../Components/ConsultationFooter';

import useMedia from 'utils/useMedia';

import BaseScene from './BaseScene';

const contentOverrideStyles = css`
  background: ${theme.colors.secondary['100']};

  padding: ${legacyTheme.spacing.s32} ${legacyTheme.spacing.s24};

  ${legacyTheme.breakpoints.up('sm')} {
    padding: ${legacyTheme.spacing.s56} ${legacyTheme.spacing.s56};
  }
`;

const Container = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: ${legacyTheme.spacing.s16};

  ${legacyTheme.breakpoints.up('sm')} {
    max-width: 667px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    max-width: 790px;
  }
`;

const fadeInKeyframes = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const Review = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: 8px;

  padding: ${legacyTheme.spacing.s24} ${legacyTheme.spacing.s16};

  background-color: white;

  ${fadeInKeyframes};

  &:nth-of-type(1) {
    animation-name: ${fadeInKeyframes.name};
    animation-fill-mode: both;
    animation-iteration-count: 1;
    animation-delay: 1s;
    animation-duration: 0.7s;
    animation-timing-function: linear;
  }
  &:nth-of-type(2) {
    animation-name: ${fadeInKeyframes.name};
    animation-fill-mode: both;
    animation-iteration-count: 1;
    animation-delay: 2.7s;
    animation-duration: 0.7s;
    animation-timing-function: linear;
  }
`;

const Description = styled(Typography)`
  .scoring-loading-description-span {
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const fadeOutKeyframes = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const AnimatedConsultationFooter = styled(ConsultationFooter)`
  ${fadeInKeyframes};
  animation-name: ${fadeInKeyframes.name};
  animation-fill-mode: both;
  animation-iteration-count: 1;
  animation-delay: 4.5s;
  animation-duration: 0.3s;
`;

const FakeAnimatedConsultationFooter = styled(ConsultationFooter)`
  ${fadeOutKeyframes};
  animation-name: ${fadeOutKeyframes.name};
  animation-fill-mode: both;
  animation-iteration-count: 1;
  animation-delay: 4.6s;
  animation-duration: 0.3s;
`;

const ReviewList = ({ contentList }) => {
  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {contentList.map(({ rating: stars, comment, displayName: name }) => (
        <Review key={name}>
          {stars && (
            <Rating
              best={5}
              score={stars}
              starVariant="reviews"
              style={{ '--space': '5px' }}
              worst={1}
            />
          )}
          <Typography align="center" color="noir" variant="p2">
            {comment}
          </Typography>
          <Typography align="center" color="noir" variant="mono3">
            {name}
          </Typography>
        </Review>
      ))}
    </div>
  );
};

ReviewList.propTypes = {
  contentList: PropTypes.arrayOf(PropTypes.shape()).isRequired,
};

const ScoringLoading = ({ title, description, reviews, disclaimer, Icon, onAnimationDone }) => {
  const isMobile = useMedia(`(max-width: ${breakpoints.sm - 1}px)`);

  return (
    <ClassNames>
      {({ css }) => (
        <BaseScene background="none" classes={{ content: css(contentOverrideStyles) }}>
          <Container>
            <Typography
              align="center"
              color="noir"
              data-testid="scoring-loading-variant-1-title"
              variant={isMobile ? 'h2' : 'h1'}
            >
              {title}
            </Typography>
            {Icon && <Icon />}
            {description && (
              <Description align="center" color="noir" variant="p2">
                {description}
              </Description>
            )}
            <ReviewList contentList={reviews} />
            {disclaimer && (
              <Typography align="center" color="noir" italic variant="p3">
                {disclaimer}
              </Typography>
            )}
          </Container>
          {/* Why Two footer ? For smoother animation transition between loading & ready state */}
          <FakeAnimatedConsultationFooter
            buttonLabel="View your results"
            disabled={false}
            isSaving
          />
          <AnimatedConsultationFooter
            buttonLabel="View your results"
            disabled={false}
            isSaving={false}
            submit={onAnimationDone}
          />
        </BaseScene>
      )}
    </ClassNames>
  );
};

ScoringLoading.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  disclaimer: PropTypes.string,
  onAnimationDone: PropTypes.func,
  Icon: PropTypes.elementType,
  reviews: PropTypes.arrayOf(
    PropTypes.shape({
      rating: PropTypes.number,
      comment: PropTypes.string,
      displayName: PropTypes.string,
    })
  ).isRequired,
};

ScoringLoading.defaultProps = {
  description: null,
  disclaimer: null,
  onAnimationDone: () => {},
  Icon: null,
};

export default ScoringLoading;
